import { Box, FormControl, InputLabel, MenuItem, Select, Switch, Typography } from '@material-ui/core';
import { EventAvailable } from '@material-ui/icons';
import React, { useEffect } from 'react';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import {
  GET_APPLICATION_ACTIVITY_AUTO_STATUS,
  GET_APPLICATION_AVAILABILITY_SIMPLE,
  GET_APPLICATION_ENROL_ACTIVITIES,
  GET_APPLICATION_AVAILABILITY_COMPLEX,
} from './admin-console-applicant-screening.gql';
import { GetApplicationAvailabilitySimple } from './__generated__/GetApplicationAvailabilitySimple';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GetApplicationAvailabilityComplex } from './__generated__/GetApplicationAvailabilityComplex';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { GetApplicationEnrolActivities } from './__generated__/GetApplicationEnrolActivities';
import { GetApplicationActivityAutoStatus } from './__generated__/GetApplicationActivityAutoStatus';
import { OnboardingStageAssignment } from './OnboardingStageAssignment';
import { OnboardingOptionContentForm } from './OnboardingOptionContentForm';

type AutoStatusOption = 'off' | 'to-team' | 'to-waitlist' | 'to-application';

const AUTO_STATUS_OPTIONS: { value: AutoStatusOption; type: string | null; label: string }[] = [
  { value: 'off', type: null, label: 'Off' },
  { value: 'to-team', type: 'application-activity-auto-to-team', label: 'Auto Add to Team' },
  { value: 'to-waitlist', type: 'application-activity-auto-to-waitlist', label: 'Auto Add to Waitlist' },
  { value: 'to-application', type: 'application-activity-auto-to-application', label: 'Auto Add to Application' },
];

export const ActivitiesAndAvailabilitiesForm = () => {
  const { setSnackbar } = useSnackbar();
  const [applicationAvailabilitySimple, setApplicationAvailabilitySimple] = React.useState(false);
  const [applicationAvailabilityComplex, setApplicationAvailabilityComplex] = React.useState(false);
  const [applicationActivityEnrolment, setApplicationActivityEnrolment] = React.useState(false);
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const { data: availabilitySimple, refetch: availabilitySimpleRefetch } = useCampfireQuery<
    GetApplicationAvailabilitySimple,
    undefined
  >(GET_APPLICATION_AVAILABILITY_SIMPLE);

  const { data: availabilityComplex, refetch: availabilityComplexRefetch } = useCampfireQuery<
    GetApplicationAvailabilityComplex,
    undefined
  >(GET_APPLICATION_AVAILABILITY_COMPLEX);

  const { data: applicationActivity, refetch: applicationActivityRefetch } = useCampfireQuery<
    GetApplicationEnrolActivities,
    undefined
  >(GET_APPLICATION_ENROL_ACTIVITIES);

  const { data: autoStatusData, refetch: autoStatusRefetch } = useCampfireQuery<
    GetApplicationActivityAutoStatus,
    undefined
  >(GET_APPLICATION_ACTIVITY_AUTO_STATUS);

  const [autoStatus, setAutoStatus] = React.useState<AutoStatusOption>('off');

  useEffect(() => {
    setApplicationAvailabilityComplex(availabilityComplex?.vm.configItem?.configItemValue?.flagValue ?? false);
    setApplicationAvailabilitySimple(availabilitySimple?.vm.configItem?.configItemValue?.flagValue ?? false);
    setApplicationActivityEnrolment(applicationActivity?.vm.configItem?.configItemValue?.flagValue ?? false);
  }, [availabilityComplex, availabilitySimple, applicationActivity]);

  useEffect(() => {
    if (autoStatusData?.vm.autoToTeam?.configItemValue?.flagValue) {
      setAutoStatus('to-team');
    } else if (autoStatusData?.vm.autoToWaitlist?.configItemValue?.flagValue) {
      setAutoStatus('to-waitlist');
    } else if (autoStatusData?.vm.autoToApplication?.configItemValue?.flagValue) {
      setAutoStatus('to-application');
    } else {
      setAutoStatus('off');
    }
  }, [autoStatusData]);

  const refetchValues = () => {
    availabilityComplexRefetch();
    availabilitySimpleRefetch();
    applicationActivityRefetch();
    autoStatusRefetch();
    setApplicationAvailabilityComplex(availabilityComplex?.vm.configItem?.configItemValue?.flagValue ?? false);
    setApplicationAvailabilitySimple(availabilitySimple?.vm.configItem?.configItemValue?.flagValue ?? false);
    setApplicationActivityEnrolment(applicationActivity?.vm.configItem?.configItemValue?.flagValue ?? false);
  };

  const onAutoStatusChange = async (newValue: AutoStatusOption) => {
    const previous = AUTO_STATUS_OPTIONS.find((option) => option.value === autoStatus);
    const next = AUTO_STATUS_OPTIONS.find((option) => option.value === newValue);

    if (previous?.type) {
      await campfireMutation.run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: { value: false, type: previous.type },
      });
    }
    if (next?.type) {
      await campfireMutation.run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: { value: true, type: next.type },
      });
    }

    setSnackbar({
      variant: 'success',
      open: true,
      message: 'Settings updated',
    });
    refetchValues();
  };

  const onValueChange = (type: string) => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/application/application-availability`,
        method: 'post',
        data: {
          value: type === 'complex' ? !applicationAvailabilityComplex : !applicationAvailabilitySimple,
          type: type,
        },
      })
      .then(() => {
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Settings updated',
        });
        refetchValues();
      });
  };

  const onActivityChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !applicationActivityEnrolment,
          type: 'application-enrol-activity',
        },
      })
      .then(() => {
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Settings updated',
        });
        refetchValues();
      });
  };

  return (
    <Box>
      <>
        <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
          <EventAvailable style={{ fontSize: '14px', paddingRight: '5px' }} /> Activities & Availabilities
        </Typography>
        <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080' }}>
          {'Settings related to applicants indicating activity and availability preferences'}
        </Typography>
        <Box>
          <Typography
            style={{ fontWeight: 600, fontSize: '15px', display: 'flex', alignItems: 'center', marginTop: '1.5rem' }}
          >
            Activities
          </Typography>
        </Box>
        <Box display='flex' flex='1 1 auto' marginTop='.5em'>
          <Box style={{ paddingTop: 4, paddingRight: 8 }}>
            <Switch
              name='auto-verification-toggle'
              color='primary'
              checked={applicationActivityEnrolment}
              onChange={() => onActivityChange()}
              inputProps={{ 'aria-label': 'verification checkbox' }}
            />
          </Box>
          <Box>
            <Box ml={1}>
              <Typography
                style={{
                  fontWeight: 500,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#333',
                }}
              >
                Activity Preferences & Enrolment
              </Typography>
              <Typography style={{ fontSize: '13px', color: '#808080' }}>
                Applicants will be able to indicate their activity preferences as part of the application workflow.{' '}
                <br />
                This will enable automatic enrolment to the activity team, transfer to the application list or waitlist
                on manager approval
              </Typography>
            </Box>
          </Box>
        </Box>
        <OnboardingStageAssignment
          optionType='activity_preferences'
          optionLabel='Activity Preferences'
          enabled={applicationActivityEnrolment}
        />
        <OnboardingOptionContentForm
          optionType='activity_preferences'
          optionLabel='Activity Preferences'
          defaultTitle='Activities'
          defaultDescription='The activities below are what you can express interest in.'
          enabled={applicationActivityEnrolment}
        />
        {applicationActivityEnrolment && (
          <Box style={{ marginTop: '20px', marginLeft: '16px' }}>
            <Typography
              variant='subtitle2'
              style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '12px',
                fontWeight: 600,
              }}
            >
              Automatic Applications
            </Typography>
            <FormControl variant='outlined' size='small' style={{ minWidth: '200px' }}>
              <InputLabel id='application-activity-auto-status-select'>Automatic Action</InputLabel>
              <Select
                labelId='application-activity-auto-status-select'
                value={autoStatus}
                onChange={(event) => onAutoStatusChange(event.target.value as AutoStatusOption)}
                label='Automatic Action'
                disabled={campfireMutation.isLoading}
              >
                {AUTO_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Typography variant='body2'>{option.label}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              Choose what happens automatically when an applicant indicates an activity preference, instead of requiring
              a manager to action it. &quot;Off&quot; keeps the current manual review behaviour.
            </Typography>
          </Box>
        )}
        <Box>
          <Typography
            style={{ fontWeight: 600, fontSize: '15px', display: 'flex', alignItems: 'center', marginTop: '1.5rem' }}
          >
            Availabilities
          </Typography>
          <Typography style={{ fontSize: '13px', color: '#808080' }}>
            You may only enable one of the following
          </Typography>
        </Box>
        <Box display='flex' flex='1 1 auto' marginTop='1em'>
          <Box style={{ paddingTop: 4, paddingRight: 8 }}>
            <Switch
              name='auto-verification-toggle'
              color='primary'
              checked={applicationAvailabilitySimple}
              onChange={() => onValueChange('simple')}
              inputProps={{ 'aria-label': 'verification checkbox' }}
            />
          </Box>
          <Box>
            <Box ml={1}>
              <Typography
                style={{
                  fontWeight: 500,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#333',
                }}
              >
                Simple Availability
              </Typography>
              <Typography style={{ fontSize: '13px', color: '#808080' }}>
                Applicants can indicate availability to managers to in a simple weekday and time structure. <br />
                This information will not transfer to visible availability on roster sessions.
              </Typography>
            </Box>
          </Box>
        </Box>
        <OnboardingStageAssignment
          optionType='availability_simple'
          optionLabel='Simple Availability'
          enabled={applicationAvailabilitySimple}
        />
        <OnboardingOptionContentForm
          optionType='availability_simple'
          optionLabel='Simple Availability'
          defaultTitle='Availability'
          defaultDescription='Choose your available days and times from the table below.'
          enabled={applicationAvailabilitySimple}
        />
        <Box display='flex' flex='1 1 auto' marginTop='1.5rem'>
          <Box style={{ paddingTop: 4, paddingRight: 8 }}>
            <Switch
              name='auto-verification-toggle'
              color='primary'
              checked={applicationAvailabilityComplex}
              onChange={() => onValueChange('complex')}
              inputProps={{ 'aria-label': 'verification checkbox' }}
            />
          </Box>
          <Box>
            <Box ml={1}>
              <Typography
                style={{
                  fontWeight: 500,
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#333',
                }}
              >
                Enhanced Availability
              </Typography>
              <Typography style={{ fontSize: '13px', color: '#808080' }}>
                Applicants can indicate availability to managers on a date and session basis. <br />
                This information, on applicant approval, will transfer directly to visible availability on rosters.
                <br />
                For event-type activities, applicants will be able to select specific event dates and sessions they are
                interested in attending.
              </Typography>
            </Box>
          </Box>
        </Box>
        <OnboardingStageAssignment
          optionType='availability_complex'
          optionLabel='Enhanced Availability'
          enabled={applicationAvailabilityComplex}
        />
        <OnboardingOptionContentForm
          optionType='availability_complex'
          optionLabel='Enhanced Availability'
          defaultTitle='Availability'
          defaultDescription='Choose your available days and times from the table below.'
          enabled={applicationAvailabilityComplex}
        />
      </>
    </Box>
  );
};
